<template>
  <div>
    <!--
      The only <h1> in the running app (audit_web.md section 4.4). It stays an
      h1: naming the product at the top of the chrome is what an h1 is for.
      The finding that section actually reports — that no PAGE has an h1, so
      the outline of /loads/1234 is the app name and then a jump to an h2 — is
      each page's to fix as it migrates, not the shell's to fix by demoting
      its own heading.
    -->
    <!--
      THE TITLE BAND (Mark, 2026-09-27: the ground behind LADEWAY "is too dark.
      It should match the other colors", with "a yellow horizontal bar across the
      bottom... similar to how we have at the bottom of the Dispatch/Create Load
      section heading bar", and "include the Ladeway Logo in that section. When
      the bar collapses, we can just show the logo").

      It was --lw-navy-950, a step DEEPER than the chassis, on the old reasoning
      that the title band should sit below the rail it tops. That reads as a
      third colour now the top bar has joined the chassis, so the band takes the
      chassis ground and the amber rule LwPageHeader already uses to close a
      heading block (`3px solid var(--lw-line-chassis)`). One surface, one rule,
      the same idiom in both places.

      THE WORDMARK IS THE LOGO, so the lockup replaces the text rather than
      sitting beside it: `ladeway-lockup-reversed.svg` is the mark AND the name,
      drawn white-on-amber for exactly this kind of dark ground. Collapsed, the
      rail is an icon strip, so it shows the mark alone.

      THE <h1> STAYS, and stays the only one in the running app (audit_web.md
      4.4). It is visually hidden rather than removed: the document outline still
      needs a name at the top of the chrome, and a screen reader reading "Ladeway"
      is not served by an <img> alt inside a heading that has no text of its own.
      The images are `alt=""` and aria-hidden for the same reason -- they would
      otherwise say the name a second time.
    -->
    <div id="app-title">
      <h1 class="lw-p-app-title__name">{{ appTitle }}</h1>
      <img
        v-if="!collapsed"
        class="lw-p-app-title__lockup"
        src="/static/brand/ladeway-lockup-reversed.svg"
        alt=""
        aria-hidden="true"
      />
      <img
        v-else
        class="lw-p-app-title__mark"
        src="/static/brand/ladeway-mark-reversed.svg"
        alt=""
        aria-hidden="true"
      />
    </div>

    <div id="nav-user">
      <nav-user :user="user" />
    </div>

    <nav-component id="nav-list" />

    <nav-sign-out />

    <!--
      THE RAIL TOGGLE (Mark, 2026-09-27: "there's probably a better interface we
      can have in the sidenav to collapse the bar, but I don't know what that
      is. Open to you figuring that out").

      WHY IT IS HERE AND NOT IN THE TOP BAR. Collapsing the rail is a thing you
      do to the rail, and on a desk the rail never goes away when it collapses --
      it narrows to icons (navLink.vue hides the labels, the title shortens to
      "L"). So the control can live inside the thing it controls and still be
      there to undo itself, which a control in the top bar could never explain.

      WHY THE BOTTOM. The top of the rail is the product's name, and a chevron
      next to a wordmark competes with it. The bottom is where the rail's other
      meta control already is (Sign out), it is the furthest point from the nav
      entries somebody is actually aiming at, and it is where every tool that
      does this well puts it.

      WHY A CHEVRON AND NOT A HAMBURGER. A hamburger means "here is a menu",
      which is wrong twice over: the menu is already on screen, and the button
      does not open it. A chevron pointing at the edge it will move toward says
      which way the rail is about to go, and it flips when the rail does.

      NOT RENDERED ON A PHONE. Under 768px `collapsed` is not a narrow rail, it
      is a hidden layer (app.scss: translateX(-100%) and visibility: hidden), so
      a button in here would be unreachable in exactly the state a person would
      want it. The narrow-screen affordance is the header's menu button and the
      backdrop behind the open sheet.
    -->
    <button
      v-if="!narrow"
      type="button"
      class="lw-p-nav-rail__toggle"
      :aria-expanded="String(!collapsed)"
      aria-controls="nav-list"
      :aria-label="collapsed ? 'Expand the navigation rail' : 'Collapse the navigation rail'"
      data-test="rail-toggle"
      @click="$emit('toggleRail')"
    >
      <i
        class="fa"
        :class="collapsed ? 'fa-angle-double-right' : 'fa-angle-double-left'"
        aria-hidden="true"
      />
      <span v-if="!collapsed" class="lw-p-nav-rail__toggle-name">Collapse</span>
    </button>
  </div>
</template>

<script>
import navUser from './navUser.vue';
import navComponent from './nav/nav.vue';
import navSignOut from './navSignOut.vue';
export default {
  components: { navUser, navComponent, navSignOut },
  props: {
    appTitle: {
      type: String,
      default: 'App Title',
    },
    collapsed: {
      required: true,
      type: Boolean,
    },
    // app.vue's, from the matchMedia it already watches. The rail toggle is
    // desktop-only; see its comment in the template.
    narrow: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  emits: ['toggleRail'],
  // `shortTitle` was here and is gone: it turned "Ladeway" into "L" for the
  // collapsed band, and the collapsed band shows the brand mark now. Nothing
  // rendered it any more, so the only thing keeping it alive was its own two
  // tests -- which is the shape dead code takes when a suite outlives a feature.
};
</script>

<style scoped>
/*
  Design refresh phase 2, surface 4 (the shell). Scoped, where it was global,
  and on the type and spacing scales rather than on four hand-picked rem
  values.

  The band's colour is NOT here: `#app-title` gets its navy-950 ground from
  ui/src/sass/app.scss, alongside #sidebar, #header, #main and #content, which
  is where the shell's five layout ids are painted as one system. Splitting
  half of it into this file would put the band's background and the rail's
  background in two files that have to agree.
*/
#app-title {
  align-items: center;
  display: flex;
  height: 3rem;
  justify-content: center;
  /* The ground is in app.scss with the other shell ids; these are the two solid
     amber lines that close the band -- a DOUBLE YELLOW, the roadway marking you
     may not cross (Mark, 2026-09-27).

     This is half of one marking. #header carries the same geometry with its
     LOWER line broken instead of solid, so read across the top of the app the
     marking is double-solid over the rail and solid-over-broken over the bar,
     changing character exactly at the rail's edge the way a road's does.

     THE GEOMETRY MUST MATCH #header's or the marking steps at that edge: 2px
     lines, a 3px gap, the lower line on the band's bottom edge, both bands 3rem.
     app.scss carries the full reasoning, including why these are background
     layers rather than borders (the browser picks a border's dash length; a
     gradient does not). */
  background-image:
    linear-gradient(var(--lw-line-chassis), var(--lw-line-chassis)),
    linear-gradient(var(--lw-line-chassis), var(--lw-line-chassis));
  background-size:
    100% 2px,
    100% 2px;
  background-position:
    left bottom 5px,
    left bottom;
  background-repeat: no-repeat;
  box-sizing: border-box;
  /* STICKY, like #main beside it (Mark, 2026-10-10). On a short window the
     rail is taller than the viewport and scrolling over it scrolls the page;
     #main is pinned, so this band used to scroll away from #header and the
     marking came apart at the rail's edge. Pinned the same way, the two bands
     stay one marking while the rail's entries scroll beneath. The ground is
     opaque (app.scss), so nothing shows through it. */
  position: sticky;
  top: 0;
  z-index: 1;
}

/*
  Visually hidden, not removed: the app's only <h1> still has to name the
  product for the document outline and for a screen reader, while the lockup
  carries it on screen. The clip-rect shape is the one LwButton's own
  __sr-only class uses.
*/
.lw-p-app-title__name {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

/* Bounded by HEIGHT, not width: the lockup is a wide aspect (1095x252) and the
   band is 3rem, so a width-driven size would overflow the rail. `auto` width
   with a max keeps it inside the 208px rail at every zoom level. */
.lw-p-app-title__lockup {
  display: block;
  height: 1.5rem;
  width: auto;
  max-width: calc(100% - var(--lw-space-6));
}

/* The mark alone is nearly square (200x220), so it is sized to the band's own
   rhythm rather than to the lockup's height. */
.lw-p-app-title__mark {
  display: block;
  height: 1.75rem;
  width: auto;
}

#nav-user {
  padding: var(--lw-space-4);
  padding-top: var(--lw-space-5);
  padding-bottom: var(--lw-space-2);
}

/*
  Dressed as a rail entry, deliberately identical to Sign out's treatment
  (navSignOut.vue): same ink, same label type, same dimmed-until-hovered
  opacity, same navy-950 hover fill. It is a meta control at the bottom of the
  rail and it should look like the other one rather than like a new kind of
  thing.

  `margin-top: auto` is what pins the pair to the bottom of a tall rail. It
  needs the sidebar's own root to be a column flexbox, which is set below on
  :host-ish terms -- see the comment there.
*/
.lw-p-nav-rail__toggle {
  appearance: none;
  align-items: center;
  background: none;
  border: 0;
  border-left: 3px solid transparent;
  color: var(--lw-fg-inverse);
  cursor: pointer;
  display: flex;
  font: var(--lw-type-label);
  gap: var(--lw-space-2);
  letter-spacing: var(--lw-type-label-tracking);
  opacity: 0.62;
  padding: var(--lw-space-2) var(--lw-space-6);
  text-align: left;
  text-transform: var(--lw-type-label-transform);
  transition: opacity var(--lw-motion-fast) var(--lw-motion-ease);
  width: 100%;
}

.lw-p-nav-rail__toggle:hover,
.lw-p-nav-rail__toggle:focus-visible {
  background-color: var(--lw-navy-950);
  opacity: 1;
}

/* Centred with no label when the rail is narrow, the rule every other rail
   entry follows in that state (navLink.vue, navSignOut.vue). */
#sidebar.collapsed .lw-p-nav-rail__toggle {
  justify-content: center;
  padding-left: var(--lw-space-2);
  padding-right: var(--lw-space-2);
}
</style>
